import { STORAGE_KEYS, removeKeys, currentUserId } from './storage';

// Dev quick-login helpers — LOCALHOST ONLY. Caliber's "auth" is just a user_id
// in localStorage (the backend forwards it to Paradigm as X-User-ID), so a dev
// login seeds that id + the onboarding flags, bypassing the Paradigm OAuth.
// Point VITE_DEV_USER_ID at a real authorized Paradigm user (e.g. an app test
// user) for real graph reads/writes; otherwise the id is a placeholder.
export const DEV_LOGIN_ENABLED = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);

/** The id a dev session falls back to when VITE_DEV_USER_ID is unset. It names
 *  nobody on the graph, which is what makes it safe to answer with fixtures —
 *  and it is the ONLY id that may be answered that way. Exported because
 *  devSeed's intercept gate keys on this constant, never on DEV_USER_ID: the
 *  whole point of VITE_DEV_USER_ID is that it can be a REAL user, and a gate
 *  comparing against it would then intercept exactly the session that asked
 *  for live data. */
export const PLACEHOLDER_USER_ID = '00000000-0000-0000-0000-000000000001';

export const DEV_USER_ID = (import.meta.env.VITE_DEV_USER_ID as string | undefined)
  || PLACEHOLDER_USER_ID;

/** The server-side half of a dev session: a cookie for the same id, minted by
 *  POST /api/auth/dev-login when the server runs with KALIBR_DEV_LOGIN=1
 *  (lib/session-gate.js refuses a user_id it cannot match to a cookie).
 *  Best-effort — without it the seeded fixtures still render; only REAL graph
 *  reads (a VITE_DEV_USER_ID) need the server to believe the id. */
export async function mintDevSession(userId: string): Promise<{ ok: boolean; graph: boolean }> {
  try {
    const r = await fetch('/api/auth/dev-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!r.ok) console.warn(`[dev] no server session (HTTP ${r.status}) — set KALIBR_DEV_LOGIN=1 on the server for real graph reads`);
    const body = r.ok ? await r.json().catch(() => null) : null;
    return { ok: r.ok, graph: body?.graph !== false };
  } catch {
    return { ok: false, graph: true }; // unknown: keep the configured id
  }
}

/** The dev session's id. A VITE_DEV_USER_ID is a REAL user, worth signing in
 *  as only when the server can read their graph: a keyless server (the mocked
 *  browser suite, a fresh checkout — POST /api/auth/dev-login says `graph:
 *  false`) answers every typed read 503, so the session falls back to the
 *  placeholder and devSeed's fixtures. */
async function seedDevSession() {
  let userId = DEV_USER_ID;
  const minted = await mintDevSession(userId);
  if (userId !== PLACEHOLDER_USER_ID && !minted.graph) {
    console.warn('[dev] the server has no Paradigm key — signing in as the fixture placeholder, not VITE_DEV_USER_ID');
    userId = PLACEHOLDER_USER_ID;
    await mintDevSession(userId);
  }
  localStorage.setItem(STORAGE_KEYS.paradigmUserId, userId);
  localStorage.setItem(STORAGE_KEYS.registration, JSON.stringify({ name: 'Dev User', email: 'dev@caliber.local' }));
  localStorage.setItem(STORAGE_KEYS.step1Complete, 'true'); // OAuth (step 1) is bypassed for dev
}

// Reset onboarding progress + data so the dev runs the flow fresh from the
// résumé front door. Clears any stale "already onboarded" session (the common
// reason the app keeps skipping straight to /skills).
export async function startDevOnboarding() {
  await seedDevSession();
  removeKeys([
    STORAGE_KEYS.step2Complete, STORAGE_KEYS.step3Complete, STORAGE_KEYS.step4Complete,
    STORAGE_KEYS.cardMinted, STORAGE_KEYS.targetAnswered, STORAGE_KEYS.cardReviewed,
    // the walk from its first step, the welcome (the smoke test, 2026-10-05:
    // "Start onboarding" skipped it)
    STORAGE_KEYS.welcomed,
    STORAGE_KEYS.role, STORAGE_KEYS.skills, STORAGE_KEYS.hiddenSkills,
    STORAGE_KEYS.resumeNodeId, STORAGE_KEYS.resumeFilename, STORAGE_KEYS.nextStep,
    // A fresh run meets the product tour again at its end (lib/tour.ts).
    STORAGE_KEYS.tour,
  ]);
}

// SPOOF MODE (npm run spoof, scripts/spoof/run.js): the page is served for a
// server whose Paradigm is a seeded fake, and signs itself in as the seeded
// persona — onboarded, on the dev cookie. True when this call changed the
// session (the caller reloads, so every read starts as that person). The
// cookie is minted on EVERY boot, not only the first: the spoof server keeps
// sessions in memory, so a restart forgets a persona the page still names.
export const SPOOF_USER_ID = import.meta.env.VITE_SPOOF_USER_ID as string | undefined;
// A STRANGER IN THE SPOOF (a reader's view, signed out): a tab with SPOOF_STRANGER set in its sessionStorage stays signed
// out, so the public page, a card's link and the viewer read as anyone else would (scripts set it before the page loads;
// in a browser, sessionStorage.setItem('caliber:spoof-stranger', '1') and reload in a private window)
export const SPOOF_STRANGER = 'caliber:spoof-stranger';
export async function enterSpoof(): Promise<boolean> {
  if (!DEV_LOGIN_ENABLED || !SPOOF_USER_ID) return false;
  if (sessionStorage.getItem(SPOOF_STRANGER) === '1') { localStorage.clear(); return false; }
  if (currentUserId() === SPOOF_USER_ID) {
    await mintDevSession(SPOOF_USER_ID);
    return false;
  }
  localStorage.clear();
  localStorage.setItem(STORAGE_KEYS.paradigmUserId, SPOOF_USER_ID);
  // a new member (npm run spoof:blank) arrives as ofSelf's sign-up sends one: signed in (step 1), onboarding not begun
  localStorage.setItem(STORAGE_KEYS.step1Complete, 'true');
  if (!import.meta.env.VITE_SPOOF_NEW) {
    localStorage.setItem(STORAGE_KEYS.registration, JSON.stringify({ name: import.meta.env.VITE_SPOOF_NAME || 'Spoof User' }));
    for (const key of [STORAGE_KEYS.step2Complete, STORAGE_KEYS.step3Complete, STORAGE_KEYS.step4Complete,
      STORAGE_KEYS.cardMinted, STORAGE_KEYS.targetAnswered]) localStorage.setItem(key, 'true');
  }
  await mintDevSession(SPOOF_USER_ID);
  return true;
}
